import { config, devLog } from "./config";

// API Response types
type StrapiResponse<T> = {
  data: T;
};

export type StrapiImageFormat = {
  url: string;
  width: number;
  height: number;
};

export type StrapiImage = {
  url: string;
  alternativeText?: string;
  formats?: {
    thumbnail?: StrapiImageFormat;
    small?: StrapiImageFormat;
    medium?: StrapiImageFormat;
    large?: StrapiImageFormat;
  };
};

export type Page = {
  documentId: string;
  title: string;
  slug: string;
  // description?: string;
  // isInNavbar: boolean;
  navbarOrder: number;
  landingPages?: PostLanding[];
  publishedAt?: string;
  locale: string;
};

export type PostGroup = {
  documentId: string;
  title: string;
  slug: string;
  article?: any[]; // Strapi blocks format
  image?: StrapiImage | null;
  thumbnail: StrapiImage;
  createdAt: string;
  publishedAt?: string;
  locale: string;
  date: string
};

export type PostLanding = {
  documentId: string;
  // title: string;
  slug: string;
  // heroTitle: string;
  // heroSubtitle?: string;
  image?: StrapiImage | null;
  content?: any[]; // Strapi blocks format
  associatedPages?: Page[];
  publishedAt?: string;
  locale: string;
};

type PagesResponse = StrapiResponse<Page[]>;
type PostsResponse = StrapiResponse<PostGroup[]>;
type LandingPagesResponse = StrapiResponse<PostLanding[]>;

const API_URL = config.API_URL;

// Enhanced fetch with error handling and logging
async function apiCall<T>(url: string): Promise<T> {
  devLog(`Fetching: ${url}`);
  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`API Error: ${res.status} ${res.statusText}`);
  }

  const data = await res.json();
  devLog(`Received:`, data);

  // Log locale information for debugging
  if (data.data && Array.isArray(data.data)) {
    console.log(`API Response for ${url}:`, {
      count: data.data.length,
      locales: data.data.map((item: any) => ({
        title: item.title,
        slug: item.slug,
        locale: item.locale,
      })),
    });
  }

  return data;
}

// Fetch all published pages
export async function fetchPages(locale: string = "fi") {
  const data = await apiCall<PagesResponse>(
    `${API_URL}/pages?populate=*&sort=navbarOrder:asc&locale=${locale}`
  );
  const filteredPages = data.data.filter(
    (page) => page.publishedAt && page.locale === locale
  );
  // Ensure proper sorting by navbarOrder
  return filteredPages.sort((a, b) => a.navbarOrder - b.navbarOrder);
}

// Fetch posts for a specific page
export async function fetchPosts(pageSlug: string, locale: string = "fi") {
  const data = await apiCall<PostsResponse>(
    `${API_URL}/posts?populate=*&filters[pages][slug][$eq]=${pageSlug}&locale=${locale}`
  );
  // Filter by locale again to ensure we get the right language
  return data.data.filter((post) => post.publishedAt && post.locale === locale);
}

// Fetch a single post by slug
export async function fetchPost(slug: string, locale: string = "fi") {
  const data = await apiCall<PostsResponse>(
    `${API_URL}/posts?populate=*&filters[slug][$eq]=${slug}&locale=${locale}`
  );
  return data.data.filter((post) => post.publishedAt && post.locale === locale);
}

// Fetch all landing pages
export async function fetchLandingPages(locale: string = "fi") {
  const data = await apiCall<LandingPagesResponse>(
    `${API_URL}/landings?populate=*&sort=createdAt:desc&locale=${locale}`
  );
  return data.data.filter(
    (landing) => landing.publishedAt && landing.locale === locale
  );
}

// Fetch landing page by slug
export async function fetchLandingPageBySlug(
  slug: string,
  locale: string = "fi"
) {
  const data = await apiCall<LandingPagesResponse>(
    `${API_URL}/landings?populate=*&filters[slug][$eq]=${slug}&locale=${locale}`
  );
  const filtered = data.data.filter(
    (landing) => landing.publishedAt && landing.locale === locale
  );
  return filtered.length > 0 ? filtered[0] : null;
}

// Fetch landing pages for a specific page
export async function fetchLandingPagesForPage(
  pageSlug: string,
  locale: string = "fi"
) {
  const data = await apiCall<LandingPagesResponse>(
    `${API_URL}/landings?populate=*&filters[associatedPages][slug][$eq]=${pageSlug}&locale=${locale}`
  );
  return data.data.filter(
    (landing) => landing.publishedAt && landing.locale === locale
  );
}

// Fetch all landing pages (since landingType is removed)
export async function fetchAllLandingPages(locale: string = "fi") {
  return await fetchLandingPages(locale);
}
